import type { ReactNode } from 'react';
import { SkeletonButton, SkeletonPageHeader, SkeletonPanel, SkeletonRect, SkeletonTable, SkeletonText } from '../Skeleton';

// =============================================================================
// Management history skeleton.
// Mirrors: PageHeader + filter panel + count + table + pagination.
// =============================================================================

export function ManagementHistorySkeleton({ isBo = false }: { isBo?: boolean }): JSX.Element {
  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-none">
        <SkeletonPageHeader className="mb-6" />

        <SkeletonPanel
          header
          title={<SkeletonText width={32} height="0.85em" />}
          trailing={<span />}
          className="mb-4"
        >
          <div className="flex flex-wrap items-end gap-3 p-4">
            {isBo && <FilterControl width={160} labelWidth={26} />}
            <FilterControl width={150} labelWidth={26} />
            <FilterControl width={130} labelWidth={26} />
            <FilterControl width={140} labelWidth={40} />
            <FilterControl width={140} labelWidth={40} />
            <FilterControl width={220} labelWidth={isBo ? 105 : 65}>
              <SkeletonButton width={58} height={38} />
            </FilterControl>
            <div className="ml-auto flex flex-col gap-1">
              <SkeletonText width={28} height="0.8em" className="invisible" />
              <SkeletonButton width={110} height={38} />
            </div>
          </div>
        </SkeletonPanel>

        <SkeletonText width={80} height="1em" className="mb-2" />

        <SkeletonPanel header={false} className="overflow-hidden">
          <SkeletonTable
            rowCount={10}
            columns={[
              ...(isBo ? [{ label: '조직명', width: '18%' }] : []),
              { label: '아이디', width: '14%' },
              { label: '메뉴', width: '11%' },
              { label: '구분', width: '11%' },
              { label: '내용', width: '26%' },
              { label: '일시', width: '18%' },
              // 수행자 식별자 복사 열(검수 S17) — 표와 칸 수를 맞춘다.
              { label: '원본', width: '8%' },
            ]}
          />
        </SkeletonPanel>

        <div className="mt-4 flex items-center justify-end gap-3">
          <SkeletonRect width={86} height={34} />
          <div className="flex items-center gap-1">
            <SkeletonRect width={38} height={38} />
            <SkeletonRect width={38} height={38} />
            <SkeletonText width={48} height="0.9em" className="mx-2" />
            <SkeletonRect width={38} height={38} />
            <SkeletonRect width={38} height={38} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterControl({
  width,
  labelWidth,
  children,
}: {
  width: number;
  labelWidth: number;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <SkeletonText width={labelWidth} height="0.8em" />
      <div className="flex gap-2">
        <SkeletonRect width={width} height={38} />
        {children}
      </div>
    </div>
  );
}
